import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GSProduct {
  id: string;
  name: string;
  description: string | null;
  product_type: string;
  certification_status: string;
  manufacturer: string | null;
  created_at: string;
  portfolio: {
    id: string;
    title: string;
  };
}

export const useGSProduct = (productId: string) => {
  const [product, setProduct] = useState<GSProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        const { data, error: fetchError } = await supabase
          .from('products')
          .select(`
            id,
            name,
            description,
            product_type,
            certification_status,
            manufacturer,
            created_at,
            portfolio:portfolios!inner(
              id,
              title
            )
          `)
          .eq('id', productId)
          .eq('portfolio.user_id', user.id)
          .single();

        if (fetchError) throw fetchError;

        setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch product');
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    } else {
      setIsLoading(false);
    }
  }, [productId]);

  return {
    product,
    isLoading,
    error,
  };
};